import React, { useEffect, useState } from "react";
import { Backdrop, Button, LogoLoader, Modal, showToast, StatusTags, TableAdv } from "../../../../../../components";
import { mapTableRows } from "../../../../../../utils";
import { fetchAllAiCallsApi, updateAiCallApi } from "../../../../../../redux/slices/aicalls/aicallsApi";
import { convertFromUTC, extractDateAndTimeFromFormatted } from "../../../../../../utils/dateUtils";
import { totalCountStart } from "../../../../../../redux/slices/totalCount/totalCountSlice";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "../../../../../../hooks";

const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const optionsDate = { year: "numeric", month: "long", day: "2-digit" };
  const formattedDate = date.toLocaleDateString("en-US", optionsDate);

  const optionsTime = {
    hour: "2-digit",
    minute: "2-digit",
    // second: "2-digit",
    hour12: true,
  };
  const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

  return `${formattedDate} ${formattedTime}`;
};

const JobBoardCandidates = ({ jobPostId }) => {
  const [loading, setLoading] = useState(false);
  const [aiCalls, setAiCalls] = useState([]);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelCallId, setCancelCallId] = useState();

  const dispatch = useDispatch();
  const { page, limit, setPage, handleLimitChange } = usePagination(10, (newPage, newLimit) => {});
  const { value } = useSelector((state) => state.totalCount);
  const totalCount = value?.jobboardcandidates?.data || 0

  const aiCallStatusMap = {
    1: "Initiated",
    2: "Success",
    3: "Failed",
    4: "Skipped",
    5: "Scheduled",
    6: "Not Answered",
    7: "Interested",
    8: "Not Interested",
    9: "Cancelled"
  }

  const columns = [
    {
      key: "candidateName",
      label: "Candidate Name",
      columnMenu: false,
    },
    {
      key: "email",
      label: "Email",
      columnMenu: false,
    },
    {
      key: "mobile",
      label: "Mobile",
      columnMenu: false,
    },
    {
      key: "location",
      label: "Location",
      columnMenu: false,
    },    
    {
      key: "source",
      label: "Source",
      columnMenu: false,
    },
    {
      key: "status",
      label: "Call Status",
      columnMenu: false,
      render: (row) => <StatusTags tag={row.status} />,
    },
    {
      key: "addedBy",
      label: "Added By",
      columnMenu: false,
      render: (row) => row?.userType === 2 ? (
        <div className="flex items-center gap-1">
          <Button type="icon" color="danger" variant="text" icon="android" className="!cursor-default"/>
          <span className="text-sm">{row.addedBy}</span>
        </div>
      ) : (<span className="text-sm">{row.addedBy}</span>)
    },
    {
      key: "addedOn",
      label: "Added On",
      columnMenu: false,
    },
  ];

  const rows = aiCalls.map((call) => {
    return {
      candidateName: `${call?.candidateId?.firstName || ""} ${call?.candidateId?.lastName || ""}`,
      email: call?.candidateId?.email?.primary?.email || "",
      mobile: `${call?.candidateId?.mobile?.primary?.code} ${call?.candidateId?.mobile?.primary?.number}`,
      location: `${call?.candidateId?.location?.city}, ${call?.candidateId?.location?.state}`,
      status: aiCallStatusMap[call?.status],
      uiid: call?.uiid,
      addedBy: `${call?.candidateId?.addedBy?.firstName} ${call?.candidateId?.addedBy?.lastName}`,
      userType: call?.candidateId?.addedBy?.userType || 1,
      addedOn: formatDateTime(convertFromUTC(call?.candidateId?.addedOn)),
      source: call?.source === 2 ? "Career Builder" : "Monster"
    };
  });

  const fetchAiCalls = async () => {
    try {
      setLoading(true);
      const payload = {
        coll: "AiCall",
        fil: { 
          jobPostId: { type: "NONE", value: jobPostId },
          source: { type: "IN", value: [2,3] }
        },
        ref: "Candidate JobPost",
        incl: ["uiid firstName lastName email mobile location jobTitle addedBy addedOn", "uiid jobId"],
        extret: "uiid firstName lastName userType",
        lim: limit,
        pag: page,
      }
      const response = await fetchAllAiCallsApi(payload);

      if (response?.success) {
        setAiCalls(response?.data?.data);
      } else if (response?.msg?.includes("You've reached the end")) {
        setAiCalls([]);
      }
    } catch (error) {
      console.error("Error fetching AI Calls:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountOfAiCalls = () => {
    dispatch(
      totalCountStart({
        collection: "aicalls",
        coll: "AiCall",
        filters: { 
          jobPostId: { type: "NONE", value: jobPostId },
          source: { type: "IN", value: [2,3] }
        },
        tname: "jobboardcandidates",
        app: "ats",
      })
    );
  };

  useEffect(() => {
    fetchCountOfAiCalls();
  }, []);

  useEffect(() => {
    fetchAiCalls();
  }, [jobPostId, page, limit, value?.jobboardcandidates]);

  const mappedRows = mapTableRows(rows, columns);
  return (
    <div className="flex flex-col mt-5 overflow-auto">
      <div className="flex flex-grow overflow-auto gap-4 mx-0.5">
        <TableAdv
          showSlNo
          columns={columns}
          rows={mappedRows}
          totalCount={totalCount}
          serverPagination={true}
          page={page}
          limit={limit}
          toolbarRight={{
            fullScreen: true,
          }}
        />
      </div>

      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default JobBoardCandidates;
